/* global Sys, Services, Utils */
Sys.ns("Services");

/**
 * The Plugin Menu Service.
 *
 * @class Services.PluginMenu
 * @extends Services.Base
 */
Services.PluginMenu = {
    constructor: function() {
        Services.PluginMenu.superclass.constructor.apply(this, arguments);

        this._enabled = false;
        this._icon = null;
        this._initialized = false;
        this._notification = false;
        this._open = false;
        this._overlay = null;
        this._sizeOptions = null;
    },

    _setupEvents: function() {
        this.observable.on({
            "request:pluginMenu.init": this._init.bind(this),
            "request:pluginMenu.activate": this.open.bind(this),
            "request:pluginMenu.deactivate": this.close.bind(this),
            "request:pluginMenu.notification": this.showNotification.bind(this),
            "request:pluginMenu.changeMenuHeight": this.changeHeight.bind(this),
            "request:pluginMenu.enable": this.enable.bind(this),
            "request:pluginMenu.disable": this.disable.bind(this),
            "notify:viewport.scaled": this._adjustPluginOnViewportResize.bind(this),

            "notify:platform.orientationChanged": this._orientationChanged.bind(this),
            "notify:stateHandler.enteringIdleState": this.enable.bind(this),
            "notify:stateHandler.leavingIdleState": this.disable.bind(this)
        });
    },

    /**
     * Adjust the position of plugin when game view size changes.
     *
     * @private
     * @returns {void}
     */
    _adjustPluginOnViewportResize: function() {
        window.requestAnimationFrame(function() {
            this._adjustPluginTopbar();
        }.bind(this));
    },

    /**
     * Checks if the plugin menu is enabled.
     *
     * @returns {boolean} Whether or not the plugin menu is enabled.
     */
    isEnabled: function() {
        return this._enabled;
    },

    /**
     * Checks if the plugin menu is initialized.
     *
     * @returns {boolean} Whether or not the plugin menu is initialized.
     */
    isInitialized: function() {
        return this._initialized;
    },

    /**
     * Enables the plugin menu.
     *
     * @returns {void}
     */
    enable: function() {
        if (this.isEnabled() || !this._initialized || Services.autoplay.isActive()) {
            return;
        }

        this._enabled = true;

        this.publish("enabled");
    },

    /**
     * Disables the plugin menu.
     *
     * @returns {void}
     */
    disable: function() {
        if (!this.isEnabled() || Services.autoplay.isActive()) {
            return;
        }

        this._enabled = false;

        this.publish("disabled");
    },

    /**
     * Checks if the plugin menu is open.
     *
     * @returns {boolean} Whether or not the plugin menu is open.
     */
    isOpen: function() {
        return this._open;
    },

    /**
     * Opens the plugin menu.
     *
     * @returns {void}
     */
    open: function() {
        if (this.isOpen()) {
            return;
        }

        this._open = true;

        this._iframe.style.display = "block";
        this._overlay.el.style.display = "block";

        this.publish("opened");
        this.observable.fireEvent("service:pluginMenu.opened");
        this.hideNotification();

        setTimeout(function() {
            this._adjustPluginTopbar();
        }.bind(this));
    },

    /**
     * Sets the iframe height and adjust viewport with topbar
     *
     * @returns {boolean} Indicates if new height was set.
     */

    _adjustPluginTopbar: function() {
        var newHeight,
            topBarHeight;

        if (!this._initialized) {
            return false;
        }

        topBarHeight = this._getTopBarHeight();

        if (this._open && topBarHeight > 0) {
            document.getElementById("viewport").style.marginTop = topBarHeight + "px";
        }

        newHeight = this._getNewHeight(
            this._sizeOptions,
            Utils.Platform.getOrientation(),
            topBarHeight
        );

        this._iframe.setAttribute("height", newHeight + "%");

        return true;
    },

    /**
     * returns height of the topBar
     *
     * @returns {number}
     */

    _getTopBarHeight: function() {
        if (this._open && document.getElementById("playui-topbar")) {
            return document.getElementById("playui-topbar").offsetHeight;
        }
        return 0;
    },

    /**
     * Closes the plugin menu.
     *
     * @returns {void}
     */
    close: function() {
        if (!this.isOpen()) {
            return;
        }

        this._open = false;

        this._iframe.style.display = "none";
        this._overlay.el.style.display = "none";
        this.publish("closed");
        this.observable.fireEvent("service:pluginMenu.closed");
        document.getElementById("viewport").style.marginTop = "";
    },

    /**
     * Shows the notification.
     *
     * @param {string} url The icon URL.
     * @returns {void}
     */
    showNotification: function(url) {
        if (!url) {
            return;
        }

        this._notification = true;

        this.publish("notificationShown");
        this._displayIcon(this._getIcon(url));
    },

    /**
     * Hides the active notification (if any).
     *
     * @returns {void}
     */
    hideNotification: function() {
        if (!this._notification) {
            return;
        }

        this._notification = false;
        this._displayIcon(this._icon);
    },

    /**
     * Changes the icon.
     *
     * @param {string} url The URL of the new icon.
     * @returns {void}
     */
    changeIcon: function(url) {
        this._icon = this._getIcon(url);

        this._displayIcon(this._icon);
    },

    /**
     * Set's the initial height of the plugin menu, based on in-parameters and/or max height.
     *
     * @param {number|Object} sizeOptions Ambiguous parameter, if number provided it will set height for all, else for the specific orientations.
     * @param {number} [sizeOptions.landscape] The height of the iFrame in landscape mode.
     * @param {number} [sizeOptions.portrait] The height of the iFrame in portrait mode.
     * @returns {boolean} Indicates if new height was set.
     */
    changeHeight: function(sizeOptions) {
        var newHeight;

        if (!Sys.isDefined(sizeOptions) || sizeOptions === null) {
            return false;
        }

        this._sizeOptions = sizeOptions;

        newHeight = this._getNewHeight(
            sizeOptions,
            Utils.Platform.getOrientation(),
            this._getTopBarHeight()
        );

        this._iframe.setAttribute("height", newHeight + "%");

        this.publish("changeHeight", sizeOptions);

        return true;
    },

    /**
     * Initializes the plugin menu.
     *
     * @private
     * @param {string} imageURL The image URL as a string.
     * @param {number|Object} height The height of the iFrame.
     * @param {number} [height.landscape] The height of the iFrame in landscape mode.
     * @param {number} [height.portrait] The height of the iFrame in portrait mode.
     * @param {boolean} transparency Indicates if iFrame should be transparent.
     * @returns {void}
     */
    _init: function(imageURL, height, transparency) {
        this._iframe = document.getElementById("netEntExtendPlugin");
        this._iframe.style.display = "none";
        this._createOverlay();

        this._initialized = true;

        this.changeHeight(height);
        this.changeIcon(imageURL);

        if (transparency) {
            this._iframe.style.backgroundColor = "rgba(0,0,0,0)";
        }

        this.publish("init");

        if (!this.isEnabled()) {
            // to be sure that UI responds correctly
            this.publish("disabled");
        }
    },

    /**
     * Displays the icon.
     *
     * @private
     * @param {string?} icon The icon to show or null for the default icon.
     * @returns {void}
     */
    _displayIcon: function(icon) {
        this.publish("iconChanged", icon);
    },

    /**
     * Updates the size of the iframe based on the orientation.
     *
     * @private
     * @returns {void}
     */
    _orientationChanged: function() {
        if (this._initialized) {
            this.changeHeight(this._sizeOptions);
        }
    },

    /**
     * Gets the new height (as a percentage of the viewport height).
     *
     * @private
     * @param {number|Object} sizeOptions Ambiguous parameter, if number provided it will set height for all, else for the specific orientations.
     * @param {number} [sizeOptions.landscape] The height of the iFrame in landscape mode.
     * @param {number} [sizeOptions.portrait] The height of the iFrame in portrait mode.
     * @param {string} orientation The current device orientation.
     * @param {number} topBarHeight height of the topbar.
     * @returns {number} The new height (as a percentage of the viewport height).
     */
    _getNewHeight: function(sizeOptions, orientation, topBarHeight) {
        var viewportHeight = Utils.Platform.getViewportSize().height,
            suggestedHeight = 0,
            orientationHeight,
            heightWithTopBar;

        if (typeof sizeOptions === "number") {
            suggestedHeight = sizeOptions;
        }
        else if (sizeOptions !== null && typeof sizeOptions === "object") {
            orientationHeight = sizeOptions[orientation.toLowerCase()];

            if (typeof orientationHeight === "number") {
                suggestedHeight = orientationHeight;
            }
        }

        if (suggestedHeight === 0) {
            suggestedHeight = viewportHeight;
        }

        heightWithTopBar = (suggestedHeight >= viewportHeight && orientation === "LANDSCAPE") ? viewportHeight + topBarHeight : viewportHeight;

        return (
            (Math.min(viewportHeight, suggestedHeight) / heightWithTopBar) * 100
        );
    },

    /**
     * Creates the overlay.
     *
     * @private
     * @returns {void}
     */
    _createOverlay: function() {
        this._overlay = new Sys.Element({
            tag: "div",
            id: "pluginMenuDarkOverlay",
            style: [
                "position: absolute;",
                "top: 0;",
                "left: 0;",
                "width: 100%;",
                "height: 100%;",
                "background-color: rgba(0, 0, 0, 0.6);",
                "z-index: 1000;",
                "display:none"
            ].join(""),
            renderTo: "gameWrapper",
            onClick: this.close.bind(this)
        });
    },

    /**
     * Gets the icon.
     *
     * @private
     * @param {string} url The icon URL.
     * @returns {string|null} The icon to show or null for the default icon.
     */
    _getIcon: function(url) {
        var icon = url;

        // These conditions are defined through NetEnt Extend.
        if (
            typeof icon !== "string" ||
            icon.length === 0 ||
            icon === "default"
        ) {
            icon = null;
        }

        return icon;
    }
};

Services.PluginMenu = Sys.extend(
    Services.Base,
    Services.PluginMenu,
    "Services.PluginMenu"
);
